/**
 * Frontend logging utility with hybrid console + optional Logtail reporting
 * Provides structured logging with user context from Supabase auth
 */

import { sendLogtailLog } from './logtail';
import { getVisitorIdentity } from './visitor-id';

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogContext {
  user_id?: string
  level: LogLevel
  message: string
  timestamp: string
  userAgent?: string
  visitor_id?: string
  visitor_scope?: string
  [key: string]: any
}

class FrontendLogger {
  private user_id: string | null = null;
  private enableLogtailReporting: boolean = true;

  /**
   * Set the current user ID for all subsequent logs
   */
  setuser_id(user_id: string | null) {
    this.user_id = user_id;
  }

  /**
   * Enable or disable sending logs to Logtail
   */
  setBackendReporting(enabled: boolean) {
    this.enableLogtailReporting = enabled;
  }

  private formatContext(level: LogLevel, message: string, meta?: Record<string, any>): LogContext {
    // Anonymous visitor ID lets telemetry distinguish unique people from raw
    // page loads. Never throws: telemetry must not be able to break the UI.
    let visitor: ReturnType<typeof getVisitorIdentity> = null;
    try {
      visitor = getVisitorIdentity();
    } catch {
      visitor = null;
    }

    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      user_id: this.user_id || undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      visitor_id: visitor?.visitorId,
      visitor_scope: visitor?.visitorScope,
      ...meta,
    };
  }

  private sendToLogtail(context: LogContext) {
    if (!this.enableLogtailReporting) return;

    sendLogtailLog(context.level, context.message, context);
  }

  info(message: string, meta?: Record<string, any>) {
    const context = this.formatContext('info', message, meta);
    console.info(`[INFO] ${message}`, meta || {});
    this.sendToLogtail(context);
  }

  warn(message: string, meta?: Record<string, any>) {
    const context = this.formatContext('warn', message, meta);
    console.warn(`[WARN] ${message}`, meta || {});
    this.sendToLogtail(context);
  }

  error(message: string, error?: Error | unknown, meta?: Record<string, any>) {
    const errorMeta: Record<string, any> = { ...meta };
    
    if (error instanceof Error) {
      errorMeta.error = error.message;
      errorMeta.stack = error.stack;
    } else if (error) {
      errorMeta.error = String(error);
    }

    const context = this.formatContext('error', message, errorMeta);
    if (typeof window === 'undefined') {
      console.error(`\x1b[31m[ERROR] ${message}\x1b[0m`, error, meta || {});
    } else {
      console.error(`%c[ERROR] ${message}`, 'color: red; font-weight: bold', error, meta || {});
    }
    this.sendToLogtail(context);
  }

  debug(message: string, meta?: Record<string, any>) {
    if (process.env.NODE_ENV === 'development') {
      const context = this.formatContext('debug', message, meta);
      console.debug(`[DEBUG] ${message}`, meta || {});
      this.sendToLogtail(context);
    }
  }
}

export const Logger = new FrontendLogger();
